import React, { useState } from "react";
import runtimeConfig from "runtimeConfig";
import { JA } from "utils/strings.ja";

// Each rule a new password must meet, from the user pool's policy in
// config.json. Symbols are anything other than a letter or digit — Cognito's
// own list is a subset, and it still rejects anything else on save. A
// function: config.json loads after the modules are evaluated.
const passwordRules = () => {
  const policy = runtimeConfig().passwordPolicy ?? {};
  const minLength = policy.minLength ?? 8;
  const requires = new Set(policy.characters ?? []);
  return [
    { label: JA.password.ruleMinLength(minLength), test: (pw) => pw.length >= minLength },
    requires.has("REQUIRES_LOWERCASE") && { label: JA.password.ruleLowercase, test: (pw) => /[a-z]/.test(pw) },
    requires.has("REQUIRES_UPPERCASE") && { label: JA.password.ruleUppercase, test: (pw) => /[A-Z]/.test(pw) },
    requires.has("REQUIRES_NUMBERS") && { label: JA.password.ruleNumber, test: (pw) => /[0-9]/.test(pw) },
    requires.has("REQUIRES_SYMBOLS") && { label: JA.password.ruleSymbol, test: (pw) => /[^A-Za-z0-9]/.test(pw) },
  ].filter(Boolean);
};

/** The policy checked against `password`: `{ results, meetsRules }`. */
export function usePasswordRules(password) {
  const [rules] = useState(passwordRules);
  const results = rules.map((rule) => ({ ...rule, ok: rule.test(password) }));
  return { results, meetsRules: results.every((rule) => rule.ok) };
}

const RuleItem = ({ ok, label }) => (
  <li className={`flex items-center gap-2 text-sm ${ok ? "text-green-600" : "text-slate-500"}`}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ok ? <path d="M20 6L9 17l-5-5" /> : <circle cx="12" cy="12" r="4" />}
    </svg>
    {label}
  </li>
);

/** The rules, ticked as the user types; goes under the new-password field. */
export default function PasswordRules({ results }) {
  return (
    <div className="mt-2.5 rounded-lg bg-brand-soft px-4 py-3 dark:bg-meta-4">
      <p className="mb-1.5 text-sm font-medium text-black dark:text-white">
        {JA.password.rulesTitle}
      </p>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {results.map((rule) => (
          <RuleItem key={rule.label} ok={rule.ok} label={rule.label} />
        ))}
      </ul>
    </div>
  );
}
